import React, { memo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, X, Sparkles, ArrowRight } from 'lucide-react';
import useFlowStore from '../store/flowStore';
import { getSmartSuggestions } from '../utils/flowValidator';

interface SmartSuggestionsProps {
  selectedNodeId?: string;
}

const SmartSuggestions: React.FC<SmartSuggestionsProps> = memo(({ selectedNodeId }) => {
  const { nodes, edges } = useFlowStore();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  
  useEffect(() => {
    const selectedNode = selectedNodeId 
      ? nodes.find(n => n.id === selectedNodeId)
      : undefined;
      
    const newSuggestions = getSmartSuggestions(nodes, edges, selectedNode);
    setSuggestions(newSuggestions);
    
    if (newSuggestions.length > 0) {
      setIsVisible(true);
      setIsMinimized(false);
    }
  }, [nodes, edges, selectedNodeId]);
  
  if (!isVisible || suggestions.length === 0) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={`fixed bottom-24 right-8 ${isMinimized ? 'w-12' : 'w-80'} transition-all duration-300`}
    >
      <div className="bg-gray-900 rounded-xl shadow-2xl border border-gray-800 overflow-hidden">
        {/* Header */}
        <div 
          className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-between cursor-pointer"
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="text-white" size={18} />
            {!isMinimized && (
              <h3 className="text-white font-medium text-sm">Suggerimenti Intelligenti</h3>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isMinimized && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVisible(false);
                }}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        
        {/* Content */}
        <AnimatePresence>
          {!isMinimized && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 space-y-3">
                {suggestions.map((suggestion, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="mt-1">
                      <Lightbulb className="text-yellow-500" size={16} />
                    </div>
                    <p className="text-gray-300 text-sm flex-1">{suggestion}</p>
                  </motion.div>
                ))}
                
                {/* Quick Actions */}
                <div className="pt-3 border-t border-gray-800">
                  <p className="text-xs text-gray-500 mb-2">Azioni rapide:</p>
                  <div className="space-y-1">
                    {suggestions.includes('Inizia con un nodo Trigger per definire quando si attiva l\'automazione') && (
                      <button className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 flex items-center justify-between group transition-colors">
                        <span>Aggiungi Trigger</span>
                        <ArrowRight size={14} className="text-gray-500 group-hover:text-white transition-colors" />
                      </button>
                    )}
                    {suggestions.some(s => s.includes('ritardo')) && (
                      <button className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 flex items-center justify-between group transition-colors">
                        <span>Aggiungi Delay</span>
                        <ArrowRight size={14} className="text-gray-500 group-hover:text-white transition-colors" />
                      </button>
                    )}
                    {suggestions.some(s => s.includes('Analytics')) && (
                      <button className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 flex items-center justify-between group transition-colors">
                        <span>Aggiungi Analytics</span>
                        <ArrowRight size={14} className="text-gray-500 group-hover:text-white transition-colors" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});

SmartSuggestions.displayName = 'SmartSuggestions';

export default SmartSuggestions;
